import type { ReactNode } from "react";

export type StaffSurfaceStateVariant =
  | "empty"
  | "loading"
  | "error"
  | "conflict";

export type StaffMutationOutcome = "saved" | "conflict" | "error" | "offline";

/**
 * S2-01: "the request never left this machine" is a different answer from
 * "the server said no", and it needs a different sentence: the operator can
 * fix a dropped connection, they cannot fix a 500. `staffMutation` marks the
 * lost request with 599 (see model.ts) instead of throwing.
 */
export function staffMutationOutcome(status: number): StaffMutationOutcome {
  if (status >= 200 && status < 300) return "saved";
  if (status === 409) return "conflict";
  if (status === 599) return "offline";
  return "error";
}

export function StaffSurfaceState(props: {
  variant: StaffSurfaceStateVariant;
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <section
      aria-live={props.variant === "error" || props.variant === "conflict" ? "assertive" : "polite"}
      className={`staff-surface-state ${props.variant}`}
      data-surface-state={props.variant}
      role={props.variant === "error" || props.variant === "conflict" ? "alert" : "status"}
    >
      {props.variant === "loading" && (
        <span className="staff-surface-state-pulse" aria-hidden="true" />
      )}
      <div>
        <strong>{props.title}</strong>
        <p>{props.body}</p>
      </div>
      {props.action}
    </section>
  );
}
